/**
 * Every lazy()+Suspense component gated behind useHydrated() (src/hooks/useHydrated.js),
 * listed here so App.jsx can warm their import() promises BEFORE flipping the shared
 * hydrated signal - see preloadHydratedChunks() below for why.
 */
const imports = [
  () => import('./components/UserMenu'),
  () => import('./components/auth/AuthModal'),
  () => import('./components/InstructionsModal'),
  () => import('./components/VatInfoModal'),
  () => import('./components/TrackItemModal'),
  () => import('./components/banners/MissingDomainsBanner'),
  () => import('./components/banners/AmazonCommunityBanner'),
  () => import('./components/ItemCard/AliExpressAdCard'),
  () => import('./components/ItemCard/ItemFullscreenModal'),
];

/**
 * On a /search/{slug} page a single hydrated flip used to make ~40 lazy Suspense
 * boundaries (one ItemFullscreenModal per product card, plus every other
 * hydrated-gated modal/banner) all suspend and resolve their import() in the very
 * same commit - real production stack traces (React #418/#423/#321, all under a
 * hydrateRoot -> Promise.then retry chain) showed React's Suspense retry bookkeeping
 * getting corrupted by that many simultaneous first-time resolutions, throwing an
 * invalid-hook-call error inside whichever of those components happened to mount
 * during the pile-up.
 *
 * Vite/the browser dedupes import() calls to the same module specifier against its
 * own module cache, so calling these here doesn't trigger a second network fetch for
 * chunks lazy() will request later - it just warms the cache first. Waiting for all of
 * them (each guarded by .catch so one failed chunk can't block the rest) before
 * markHydrated() flips means every one of those Suspense boundaries resolves
 * synchronously on its first render instead of suspending - so React never enters the
 * retry path this bug lives in.
 */
export function preloadHydratedChunks() {
  return Promise.all(imports.map((load) => load().catch(() => {})));
}
